/**
 * Saved restaurants page: compact table, with an optional map beside it.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, DataTableLoadMore } from "@/components/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { SearchField } from "@/components/shared/search-field";
import { GoogleMapsLoader } from "@/components/shared/google-maps-loader";
import { ViewToggle, type ViewMode } from "@/components/shared/view-toggle";
import { CITIES, CITY_OPTIONS, distanceMiles, nearestCityKey, type CityFilter, type CityKey } from "@/constants/cities";

import { useRestaurants } from "./hooks/use-restaurants";
import { useUserReviewStats } from "./hooks/use-reviews";
import { Restaurant, RestaurantFilters } from "./types/restaurants.types";
import { getRestaurantColumns, restaurantMapColumns } from "./components/restaurant-columns";
import { RestaurantExpandedRow } from "./components/restaurant-details";
import RestaurantMap from "../map/components/restaurant-map";
import { AddRestaurantModal } from "../lists/components/add-restaurant-modal";

const TABLE_PAGE_SIZE = 20;
const MAP_PAGE_SIZE = 1000;
// Matches nearestCityKey's default cutoff.
const MAP_RADIUS_MILES = 30;

const PRICE_OPTIONS = [
  { value: "all", label: "Any price" },
  { value: "0", label: "$" },
  { value: "1", label: "$$" },
  { value: "2", label: "$$$" },
  { value: "3", label: "$$$$" },
] as const;

export const SavedRestaurantsPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [city, setCity] = useState<CityFilter>("portland");
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [hoveredRestaurantId, setHoveredRestaurantId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [priceLevel, setPriceLevel] = useState<number | undefined>(undefined);

  const filters = useMemo<RestaurantFilters>(
    () => ({
      sortBy: "createdAt",
      sortOrder: "desc",
      priceLevel,
      pageSize: viewMode === "map" ? MAP_PAGE_SIZE : TABLE_PAGE_SIZE,
    }),
    [viewMode, priceLevel]
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useRestaurants(filters);

  const allRestaurants = useMemo<Restaurant[]>(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.saved?.map((saved) => ({
            ...saved.restaurant,
            lists: saved.lists || [],
            hearts: saved.hearts,
            createdAt: saved.createdAt,
          })) || []
      ) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Stats cover every saved place, not just the loaded page. Same query key as Home, so the cache is shared.
  const { data: allData } = useRestaurants({ sortBy: "createdAt", sortOrder: "desc", pageSize: 1000 });
  const { data: reviewStats } = useUserReviewStats();

  // Map mode always needs a center city; "all" means the default city on the map.
  const mapCity: CityKey = city === "all" ? "portland" : city;

  const restaurants = useMemo(() => {
    if (city === "all" && viewMode === "table") return allRestaurants;
    const center = viewMode === "map" ? CITIES[mapCity] : CITIES[city as CityKey];
    return allRestaurants.filter(
      (r) => distanceMiles(center.lat, center.lng, parseFloat(r.latitude), parseFloat(r.longitude)) <= MAP_RADIUS_MILES
    );
  }, [allRestaurants, city, mapCity, viewMode]);

  const cityCount = useMemo(
    () =>
      new Set(
        (allData?.pages ?? [])
          .flatMap((page) => page.saved ?? [])
          .map((s) => nearestCityKey(parseFloat(s.restaurant.latitude), parseFloat(s.restaurant.longitude)))
          .filter(Boolean)
      ).size,
    [allData]
  );

  // The name cell opens the row; keep the column array stable by forwarding through a ref to the latest handler.
  const rowClickRef = useRef<(restaurant: Restaurant) => void>(() => {});
  const openFromName = useCallback((restaurant: Restaurant) => rowClickRef.current(restaurant), []);
  const columns = useMemo(() => getRestaurantColumns({ onOpen: openFromName }), [openFromName]);
  const loadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);
  const handleGoogleApiLoaded = useCallback(() => setIsGoogleApiLoaded(true), []);

  const table = useReactTable({
    data: restaurants,
    columns: viewMode === "map" ? restaurantMapColumns : columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
  });

  const mapRestaurants = useMemo(
    () =>
      restaurants.map((r) => ({
        id: r.id,
        placeId: r.placeId,
        name: r.name,
        formattedAddress: r.formattedAddress,
        latitude: r.latitude,
        longitude: r.longitude,
        rating: r.rating,
        priceLevel: r.priceLevel,
        types: r.types,
        photos: r.photos,
      })),
    [restaurants]
  );

  const handleMapPinClick = (restaurant: { id: string }) => {
    setSelectedRestaurantId(restaurant.id);
    document.getElementById(`row-${restaurant.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Clicking a row (or its name) expands its details in place; in map mode it also centers the map.
  const handleRowClick = (restaurant: Restaurant) => {
    const next = selectedRestaurantId === restaurant.id ? null : restaurant.id;
    setSelectedRestaurantId(next);
    if (next && viewMode === "map") setMapCenter({ lat: parseFloat(restaurant.latitude), lng: parseFloat(restaurant.longitude) });
  };
  rowClickRef.current = handleRowClick;

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    setSorting([]);
    setSelectedRestaurantId(null);
    setMapCenter(null);
  };

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Saved" emphasis="restaurants" />
        <p className="text-[13.5px] text-muted-foreground">
          Could not load your restaurants. {error.message} Reload the page to try again.
        </p>
      </div>
    );
  }

  const footer =
    viewMode === "table" ? (
      <DataTableLoadMore
        shown={table.getRowModel().rows.length}
        total={totalCount}
        noun="places"
        hasNextPage={Boolean(hasNextPage)}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={loadMore}
      />
    ) : (
      <span>
        {restaurants.length} within {MAP_RADIUS_MILES} miles of {CITIES[mapCity].name}
      </span>
    );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Saved"
        emphasis="restaurants"
        stats={[
          { value: totalCount, label: "places" },
          { value: cityCount, label: "cities" },
          { value: reviewStats?.totalReviews ?? 0, label: "reviewed" },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField id="restaurant-search" value={globalFilter} onChange={setGlobalFilter} placeholder="Search by name, cuisine, or city" />
        <Select value={city} onValueChange={(v) => { setCity(v as CityFilter); setMapCenter(null); }}>
          <SelectTrigger className="h-8 w-[150px] text-[13px]" aria-label="City">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {CITY_OPTIONS.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priceLevel === undefined ? "all" : String(priceLevel)} onValueChange={(v) => setPriceLevel(v === "all" ? undefined : parseInt(v))}>
          <SelectTrigger className="h-8 w-[120px] text-[13px]" aria-label="Price">
            <SelectValue placeholder="Price" />
          </SelectTrigger>
          <SelectContent>
            {PRICE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <ViewToggle value={viewMode} onChange={handleViewChange} />
          <Button variant="brand" onClick={() => setIsAddOpen(true)}>
            <Plus aria-hidden /> Add restaurant
          </Button>
        </div>
      </div>

      {viewMode === "map" ? (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="sticky top-[72px] h-[calc(100vh-14rem)] overflow-hidden rounded-sm border border-foreground">
              <RestaurantMap
                height="100%"
                center={mapCenter || CITIES[mapCity]}
                savedRestaurants={mapRestaurants}
                onSavedRestaurantClick={handleMapPinClick}
                onGoogleApiLoaded={handleGoogleApiLoaded}
                hoveredId={hoveredRestaurantId}
                selectedId={selectedRestaurantId}
                zoom={mapCenter ? 15 : 12}
              />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <DataTable
              table={table}
              isLoading={isLoading && restaurants.length === 0}
              emptyMessage={`No saved places within ${MAP_RADIUS_MILES} miles of ${CITIES[mapCity].name}.`}
              selectedRowId={selectedRestaurantId}
              hoveredRowId={hoveredRestaurantId}
              onRowClick={handleRowClick}
              expandedRowId={selectedRestaurantId}
              renderExpanded={(r) => <RestaurantExpandedRow restaurant={r} />}
              onRowHoverChange={(r) => setHoveredRestaurantId(r?.id ?? null)}
              maxHeightClassName="max-h-[calc(100vh-14rem)]"
              footer={footer}
            />
          </div>
        </div>
      ) : (
        <DataTable
          table={table}
          isLoading={isLoading && restaurants.length === 0}
          emptyMessage="No saved places yet."
          emptyAction={
            <button type="button" className="text-brand underline underline-offset-[3px]" onClick={() => setIsAddOpen(true)}>
              Add your first restaurant
            </button>
          }
          onRowClick={handleRowClick}
          expandedRowId={selectedRestaurantId}
          renderExpanded={(r) => <RestaurantExpandedRow restaurant={r} />}
          footer={footer}
        />
      )}

      {viewMode !== "map" && <GoogleMapsLoader onLoaded={handleGoogleApiLoaded} />}

      <AddRestaurantModal isOpen={isAddOpen} onOpenChange={setIsAddOpen} isGoogleApiLoaded={isGoogleApiLoaded} />
    </div>
  );
};
